import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { useSettingsStore } from '@/store/settingsStore';
import { useOrganizations, useProjects } from '@/features/organizations/hooks';
import { ROUTES } from '@/lib/constants/routes';
import { announceToScreenReader } from '@/lib/utils/accessibility';

/**
 * AppLayout Component
 * 
 * Wrapper component that provides consistent layout structure for authenticated pages
 * with enhanced accessibility features.
 * 
 * Responsibilities:
 * - Render Sidebar and Topbar with proper semantic structure
 * - Provide responsive layout grid
 * - Handle mobile menu state with keyboard support
 * - Apply consistent spacing and styling
 * - Include organization/project context
 * - Handle no projects scenario with welcome page redirect
 * - Manage focus and screen reader announcements
 */

export function AppLayout() {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const { selectedOrganization, selectedProject, setProjects, setSelectedProject } = useSettingsStore();
  const { isLoading: isLoadingOrgs } = useOrganizations();
  const { projects, isLoading: isLoadingProjects } = useProjects();
  

  
  // TEMPORARILY DISABLED - This was causing infinite redirect loops
  // Use session validation in the AppLayout - this is where authenticated users will be
  // This provides background session validation without being aggressive
  // useSession();

  // Handle project selection when projects are loaded
  useEffect(() => {
    if (!isLoadingProjects && selectedOrganization && projects.length > 0) {
      console.log('AppLayout: Processing projects', {
        orgId: selectedOrganization.id,
        projectCount: projects.length,
        projectNames: projects.map(p => p.name),
        hasSelectedProject: !!selectedProject
      });
      
      // Update store with fetched projects
      setProjects(projects);
      
      // Auto-select first project if none selected
      if (!selectedProject) {
        console.log('AppLayout: Auto-selecting first project:', projects[0].name);
        setSelectedProject(projects[0]);
      }
      
      // Validate current selection still exists
      if (selectedProject && !projects.find(proj => proj.id === selectedProject.id)) {
        console.log('AppLayout: Selected project no longer exists, selecting first:', projects[0].name);
        setSelectedProject(projects[0]);
      }
    }
    
    // Clear projects when no organization selected
    if (!selectedOrganization) {
      console.log('AppLayout: No organization selected, clearing projects');
      setProjects([]);
      setSelectedProject(null);
    }
  }, [
    isLoadingProjects,
    selectedOrganization?.id,
    projects.length,
    selectedProject?.id,
    setProjects,
    setSelectedProject
  ]);

  // Handle navigation based on projects availability
  useEffect(() => {
    // Only check after organizations and projects have loaded
    if (!isLoadingOrgs && !isLoadingProjects && selectedOrganization) {
      console.log('AppLayout: Navigation effect', {
        projectsCount: projects.length,
        currentPath: location.pathname,
        hasSelectedProject: !!selectedProject,
        storeProjectsCount: useSettingsStore.getState().projects.length
      });

      // If user has projects and is on welcome page, redirect to dashboard
      if (projects.length > 0 && selectedProject && location.pathname === ROUTES.WELCOME) {
        console.log('AppLayout: Has projects and selected project, redirecting to dashboard');
        navigate(ROUTES.DASHBOARD, { replace: true });
      }
      
      // If user has organizations but no projects, redirect to welcome
      // Only redirect if we're sure there are no projects (not just loading).
      // Allow /projects so the user can actually create their first project.
      const allowedEmptyProjectRoutes: string[] = [ROUTES.WELCOME, ROUTES.PROJECTS];
      if (
        projects.length === 0 &&
        !allowedEmptyProjectRoutes.includes(location.pathname) &&
        !isLoadingProjects
      ) {
        console.log('AppLayout: No projects available, redirecting to welcome page');
        navigate(ROUTES.WELCOME, { replace: true });
      }
    }
  }, [
    isLoadingOrgs, 
    isLoadingProjects, 
    selectedOrganization, 
    projects.length,
    selectedProject,
    location.pathname, 
    navigate
  ]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        // Return focus to menu button
        const menuButton = document.querySelector('[aria-label="Toggle navigation menu"]') as HTMLElement;
        menuButton?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  // Announce context changes to screen readers
  useEffect(() => {
    if (selectedOrganization && selectedProject && !isContextLoading) {
      announceToScreenReader(
        t('accessibility.contextChanged', {
          organization: selectedOrganization.name,
          project: selectedProject.name,
          defaultValue: `Context changed to ${selectedOrganization.name} - ${selectedProject.name}`
        }),
        'polite'
      );
    }
  }, [selectedOrganization, selectedProject, t]);

  const handleMenuToggle = () => {
    setIsMobileMenuOpen((prev) => {
      const newState = !prev;
      
      // Announce state change to screen readers
      announceToScreenReader(
        newState 
          ? t('accessibility.navigationOpened', { defaultValue: 'Navigation menu opened' })
          : t('accessibility.navigationClosed', { defaultValue: 'Navigation menu closed' }),
        'polite'
      );
      
      return newState;
    });
  };

  const handleMenuClose = () => {
    setIsMobileMenuOpen(false);
    announceToScreenReader(
      t('accessibility.navigationClosed', { defaultValue: 'Navigation menu closed' }),
      'polite'
    );
  };

  // Show loading state while context is being established
  const isContextLoading = Boolean(isLoadingOrgs || (selectedOrganization && isLoadingProjects));

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation sidebar */}
      <nav 
        id="navigation"
        aria-label={t('accessibility.mainNavigation', { defaultValue: 'Main navigation' })}
        role="navigation"
      >
        <Sidebar 
          isOpen={isMobileMenuOpen} 
          onClose={handleMenuClose}
          isContextLoading={isContextLoading}
        />
      </nav>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={handleMenuClose}
          aria-hidden="true"
        />
      )}

      {/* Main content area */}
      <div className="lg:pl-64">
        {/* Top navigation bar */}
        <header role="banner">
          <Topbar 
            onMenuToggle={handleMenuToggle}
            isContextLoading={isContextLoading}
          />
        </header>
        
        {/* Main content */}
        <main 
          id="main-content"
          role="main"
          aria-label={t('accessibility.mainContent', { defaultValue: 'Main content' })}
          className="min-h-[calc(100vh-4rem)] bg-background"
        >
          {/* Show loading overlay during context switching */}
          {isContextLoading && (
            <div 
              className="fixed inset-0 z-20 bg-background/80 backdrop-blur-sm flex items-center justify-center"
              role="status"
              aria-live="polite"
              aria-label={t('accessibility.loadingContext', { defaultValue: 'Loading application context' })}
            >
              <div className="flex items-center gap-2">
                <div 
                  className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"
                  aria-hidden="true"
                ></div>
                <span className="text-sm text-muted-foreground">
                  {t('common.loadingContext', { defaultValue: 'Loading context...' })}
                </span>
              </div>
            </div>
          )}
          
          <Outlet />
        </main>
      </div>
    </div>
  );
}
